/**
 * Company Settings Form - /office/settings/company/
 *
 * Handles: logo upload/preview, CIaO toggle, VAT number live feedback,
 * phone input (intl-tel-input).
 *
 * Validation strategy: 100% server-side. Django's ``CompanySettingsForm``
 * validates phone (via ``phonenumbers``), VAT (format + checksum +
 * duplicate), BCE (model-level ``RegexValidator(^[01]\d{9}$)``), required
 * fields (ModelForm), and the cross-field "BCE/ONSS required when CIaO
 * enabled" rule. Errors come back as ``.field-group.has-error`` blocks
 * and the global ``form-error-scroll`` helper centres the first one
 * after ``htmx:afterSwap``. Submit-button protection is owned by the
 * global ``data-protect-submit`` machinery in
 * ``utils/forms/submit-button.js``.
 *
 * No client-side ``submit`` listener here on purpose — see the
 * team-form migration (``frontend/src/pages/team-form.js``) for the
 * canonical pattern.
 */

import { attachVatValidation } from '../utils/forms/vat-validation.js';
import { initAllPhoneInputs } from '../utils/forms/phone-input.js';

// ========================================================
// LOGO UPLOAD
// ========================================================

function initLogoUpload(translations) {
    var dropzone = document.getElementById('logo-dropzone');
    var fileInput = document.getElementById('id_logo');
    var emptyEl = document.getElementById('logo-empty');
    var previewEl = document.getElementById('logo-preview');
    var previewImg = document.getElementById('logo-preview-image');
    var previewTitle = document.getElementById('logo-preview-title');
    var previewMeta = document.getElementById('logo-preview-meta');
    var removeBtn = document.getElementById('logo-remove');
    var removeLogoInput = document.getElementById('remove_logo');

    if (!dropzone || !fileInput) return;

    // Idempotency guard: the htmx:afterSwap handler calls _runAutoInit with
    // forceReinit=true, which bypasses the form-level data-auto-init-done guard.
    // If autoInitCompanySettings ever fires twice on the same swapped DOM, we
    // would attach the click handler twice — then a single user click on the
    // dropzone queues TWO fileInput.click() calls, and cancelling the first
    // picker immediately reopens a second one. Tag the element so re-entry is
    // harmless.
    if (dropzone.dataset.logoInit === '1') return;
    dropzone.dataset.logoInit = '1';

    // Remember whether the page was rendered with a saved logo. If yes, clicking
    // the X must flag the server to delete it (remove_logo=1). If we're clearing
    // a newly-picked file that was never saved, there's nothing to flag.
    var hadSavedLogo = previewImg && previewImg.getAttribute('src') !== '';

    function formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        var k = 1024;
        var sizes = ['Bytes', 'KB', 'MB'];
        var i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }

    function validateFile(file) {
        var maxSize = 2 * 1024 * 1024;
        var allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (allowedTypes.indexOf(file.type) === -1) {
            alert(translations.invalidFileType || 'Invalid file type. Please upload JPG, PNG, GIF, or WebP.');
            return false;
        }
        if (file.size > maxSize) {
            alert(translations.fileTooLarge || 'File too large. Maximum size is 2MB.');
            return false;
        }
        return true;
    }

    function showEmpty() {
        emptyEl.classList.remove('hidden');
        previewEl.classList.add('hidden');
        removeBtn.classList.add('hidden');
        previewImg.src = '';
        previewTitle.textContent = '';
        previewMeta.textContent = '';
    }

    function showPreview(src, title, meta) {
        previewImg.src = src;
        previewTitle.textContent = title || '';
        previewMeta.textContent = meta || '';
        emptyEl.classList.add('hidden');
        previewEl.classList.remove('hidden');
        removeBtn.classList.remove('hidden');
    }

    function handleFile(file) {
        if (!validateFile(file)) { fileInput.value = ''; return; }
        var reader = new FileReader();
        reader.onload = function(e) {
            // User picked a new file → don't ask the backend to delete
            removeLogoInput.value = '';
            showPreview(e.target.result, file.name, formatFileSize(file.size));
        };
        reader.readAsDataURL(file);
    }

    function handleRemove() {
        fileInput.value = '';
        // Only flag the backend if the thing we're removing was actually saved.
        // Clearing a preview of a not-yet-saved file doesn't need remove_logo=1.
        if (hadSavedLogo) {
            removeLogoInput.value = '1';
            hadSavedLogo = false;
        }
        showEmpty();
    }

    dropzone.addEventListener('click', function(e) {
        if (e.target === removeBtn || removeBtn.contains(e.target)) return;
        fileInput.click();
    });

    fileInput.addEventListener('change', function() {
        if (this.files && this.files[0]) handleFile(this.files[0]);
    });

    removeBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        handleRemove();
    });

    dropzone.addEventListener('dragover', function(e) {
        e.preventDefault();
        this.classList.add('border-primary-500', 'bg-primary-50', 'dark:bg-primary-900/20');
    });
    dropzone.addEventListener('dragleave', function(e) {
        e.preventDefault();
        this.classList.remove('border-primary-500', 'bg-primary-50', 'dark:bg-primary-900/20');
    });
    dropzone.addEventListener('drop', function(e) {
        e.preventDefault();
        this.classList.remove('border-primary-500', 'bg-primary-50', 'dark:bg-primary-900/20');
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            var dt = new DataTransfer();
            dt.items.add(e.dataTransfer.files[0]);
            fileInput.files = dt.files;
            handleFile(e.dataTransfer.files[0]);
        }
    });
}

// ========================================================
// CIAO TOGGLE
// ========================================================

function initCiaoToggle() {
    var ciaoCheckbox = document.getElementById('id_ciao_enabled');
    var ciaoBlock = document.getElementById('ciao-credentials-block');
    if (ciaoCheckbox && ciaoBlock) {
        ciaoCheckbox.addEventListener('change', function() {
            ciaoBlock.classList.toggle('hidden', !ciaoCheckbox.checked);
        });
    }
}

// ========================================================
// VAT VALIDATION
// ========================================================

function initVatValidation(errorMessage) {
    var vatField = document.getElementById('vat_number_field');
    var vatInput = document.getElementById('id_vat_number');
    var form = vatInput ? vatInput.closest('form') : null;
    if (!form || !vatInput || !vatField) return;

    attachVatValidation(vatField, vatInput, form, errorMessage);
}

// ========================================================
// LIVE FEEDBACK (BCE only — phone has its own iti--error blur handler)
// ========================================================

function initBceLiveFeedback(formEl) {
    if (!formEl) return;
    // When the user starts editing the BCE field after a server-side
    // round-trip, peel back the red-error styling so the form reads as
    // "you fixed it, please try again". The actual format check is the
    // server's job (CompanySettings.bce_number model validator).
    var bceInput = document.getElementById('id_bce_number');
    if (!bceInput) return;
    bceInput.addEventListener('input', function () {
        var fg = bceInput.closest('.field-group');
        if (fg && fg.classList.contains('has-error')) {
            fg.classList.remove('has-error');
            bceInput.classList.remove('border-red-500', 'dark:border-red-500');
            bceInput.classList.add('border-gray-300', 'dark:border-gray-600');
            fg.querySelectorAll('.field-error').forEach(function (el) { el.remove(); });
        }
    });
}

// ========================================================
// MAIN INIT
// ========================================================

export function initCompanySettings(config) {
    config = config || {};
    var form = document.querySelector('form[data-auto-init="companySettings"]');
    initLogoUpload(config.translations || {});
    initCiaoToggle();
    initVatValidation(config.invalidVatMessage);
    if (form) {
        initAllPhoneInputs(form);
        initBceLiveFeedback(form);
    }
}

export function autoInitCompanySettings() {
    var form = document.querySelector('[data-auto-init="companySettings"]');
    if (!form) return;

    var ds = form.dataset;
    initCompanySettings({
        translations: {
            invalidFileType: ds.invalidFileType || '',
            fileTooLarge: ds.fileTooLarge || '',
        },
        invalidVatMessage: ds.invalidVatMessage || '',
    });
}
